import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DatasourceColumnUIModel, TableColumnsQuery } from '@y42/datasource';
import { AggregationType, QueryField } from '@y42/json-schema';
import { BehaviorSubject, startWith, Subject } from 'rxjs';
import { pairwise, withLatestFrom } from 'rxjs/operators';
import { AvailableAllChartType, ChartSettingsModel } from '../../models/widget/chart-type-constant.model';
import { WidgetBuilderModel } from '../../models/widget/widget-builder.model';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class WidgetEventEmitterService {
  constructor(private readonly tableColumnsQuery: TableColumnsQuery) {
    this.chartSettingsComponentChanged
      .pipe(
        pairwise(),
        withLatestFrom(
          this.stateSnapshotBeforeChartTypeChange.asObservable(),
          this.tableColumnsQuery.selectEnhancedUiColumns$,
          this.stateHistoryAction.pipe(startWith(null)),
        ),
        untilDestroyed(this),
      )
      .subscribe(([componentNames, stateSnapshotBeforeChartTypeChange, columnsToReAdd, stateHistoryAction]) => {
        if (stateHistoryAction) {
          // reset the last state history action
          this.stateHistoryAction.next(null);
        }
        const currentFieldNames = stateSnapshotBeforeChartTypeChange.fields.map((field) => field.field);

        const hasLastAndNewComponents = componentNames.every((component) => component && component !== '');
        const columnsToRestore =
          hasLastAndNewComponents && stateHistoryAction !== 'undo'
            ? columnsToReAdd.filter((c) => currentFieldNames.includes(c.field_name))
            : [];

        this.restoreColumns.next({
          columns: columnsToRestore,
          context: {
            fieldsSnapshot: stateSnapshotBeforeChartTypeChange.fields,
            settingsSnapshot: stateSnapshotBeforeChartTypeChange.settings,
          },
        });
      });
  }
  // drag and drop
  readonly onVariableDragStarted = new Subject<DatasourceColumnUIModel>();
  readonly onVariableDragReleased = new Subject<void>();

  readonly onColumnToggled = new Subject<{
    column: DatasourceColumnUIModel;
    action: 'selected' | 'unselected';
    context?: {
      fieldsSnapshot: QueryField[];
      settingsSnapshot: ChartSettingsModel<AvailableAllChartType>;
    };
  }>();

  readonly restoreColumns = new BehaviorSubject<{
    columns: DatasourceColumnUIModel[];
    context: {
      fieldsSnapshot: QueryField[];
      settingsSnapshot?: ChartSettingsModel<AvailableAllChartType>;
    };
  }>({ columns: [], context: { fieldsSnapshot: [] } });

  readonly stateSnapshotBeforeChartTypeChange = new Subject<WidgetBuilderModel<AvailableAllChartType>>();
  readonly chartSettingsComponentChanged = new Subject<string>();
  readonly stateHistoryAction = new Subject<'undo' | 'redo' | null>();
  /**
   * Emits a modified query field and its previous aggregation type
   */
  readonly fieldChangedAggregationType = new Subject<[QueryField, AggregationType]>();

  readonly dragInteractionHappening$ = new Subject<boolean>();
}
